// src/components/devices/SmartLockDetails.js
import React, { useState, useContext, useEffect } from 'react';
import { AppContext } from '../../context/AppContext';
import './SmartLockDetails.css';

/**
 * Component to display and manage detailed smart lock information
 */
const SmartLockDetails = ({ lock, onClose }) => {
  const { 
    getSmartLockStatus, 
    updateSmartLockAlias, 
    
    fetchPasscodes,
    addPasscode,
    deletePasscode,
    toggleSmartLock
  } = useContext(AppContext);
  
  const [isLoading, setIsLoading] = useState(false);
  const [currentLock, setCurrentLock] = useState(lock);
  const [passcodes, setPasscodes] = useState([]);
  const [selectedTab, setSelectedTab] = useState('info');
  
  // Form states
  const [lockAlias, setLockAlias] = useState(lock.lockAlias || '');
  const [autoLockTime, setAutoLockTime] = useState(lock.autoLockTime || 0);
  
  // Passcode form
  const [newPasscode, setNewPasscode] = useState('');
  const [passcodeName, setPasscodeName] = useState('');
  
  // Load lock status and passcodes on mount
  useEffect(() => {
    const loadLockData = async () => {
      try {
        setIsLoading(true);
        
        // Get latest lock status
        const status = await getSmartLockStatus(lock.lockMac);
        setCurrentLock(prev => ({ ...prev, ...status }));
        
        // Get passcodes
        if (selectedTab === 'passcodes') {
          const codes = await fetchPasscodes(lock.lockMac);
          setPasscodes(codes);
        }
      } catch (error) {
        console.error('Error loading lock data:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadLockData();
  }, [lock, selectedTab, getSmartLockStatus, fetchPasscodes]);
  
  // Handle tab change
  const handleTabChange = async (tab) => {
    setSelectedTab(tab);
    
    // Load passcodes when switching to passcodes tab
    if (tab === 'passcodes' && passcodes.length === 0) {
      try {
        setIsLoading(true);
        const codes = await fetchPasscodes(lock.lockMac);
        setPasscodes(codes);
      } catch (error) {
        console.error('Error fetching passcodes:', error);
      } finally {
        setIsLoading(false);
      }
    }
  };
  
  // Handle lock/unlock
  const handleToggle = async () => {
    try {
      setIsLoading(true);
      await toggleSmartLock(lock.lockMac, currentLock.isLocked);
      
      // Update local state
      setCurrentLock(prev => ({ ...prev, isLocked: !prev.isLocked }));
    } catch (error) {
      console.error('Error toggling lock:', error);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Handle alias update
  const handleUpdateAlias = async (e) => {
    e.preventDefault();
    
    if (!lockAlias.trim()) return;
    
    try {
      setIsLoading(true);
      await updateSmartLockAlias(lock.lockMac, lockAlias);
      
      // Update local state
      setCurrentLock(prev => ({ ...prev, lockAlias }));
    } catch (error) {
      console.error('Error updating alias:', error);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Handle auto lock time update
  const handleUpdateAutoLock = async (e) => {
    e.preventDefault();
    
    try {
      setIsLoading(true);
      await setAutoLockTime(lock.lockMac, parseInt(autoLockTime));
      
      // Update local state
      setCurrentLock(prev => ({ ...prev, autoLockTime: parseInt(autoLockTime) }));
    } catch (error) {
      console.error('Error updating auto lock time:', error);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Handle add passcode
  const handleAddPasscode = async (e) => {
    e.preventDefault();
    
    if (!newPasscode.trim() || !passcodeName.trim()) return;
    
    try {
      setIsLoading(true);
      await addPasscode(lock.lockMac, newPasscode, passcodeName);
      
      // Clear form and refresh passcodes
      setNewPasscode('');
      setPasscodeName('');
      const codes = await fetchPasscodes(lock.lockMac);
      setPasscodes(codes);
    } catch (error) {
      console.error('Error adding passcode:', error);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Handle delete passcode
  const handleDeletePasscode = async (id) => {
    try {
      setIsLoading(true);
      await deletePasscode(lock.lockMac, id);
      
      // Refresh passcodes
      const codes = await fetchPasscodes(lock.lockMac);
      setPasscodes(codes);
    } catch (error) {
      console.error('Error deleting passcode:', error);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Determine status class
  const getStatusClass = () => {
    if (!currentLock.isConnected) return 'status-disconnected';
    return currentLock.isLocked ? 'status-locked' : 'status-unlocked';
  };
  
  return (
    <div className="smart-lock-details-overlay">
      <div className="smart-lock-details-modal">
        <div className="modal-header">
          <h2>{currentLock.lockAlias || `Lock (${currentLock.lockMac})`}</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>
        
        <div className="modal-tabs">
          <button 
            className={`tab-button ${selectedTab === 'info' ? 'active' : ''}`}
            onClick={() => handleTabChange('info')}
          >
            Info
          </button>
          <button 
            className={`tab-button ${selectedTab === 'passcodes' ? 'active' : ''}`}
            onClick={() => handleTabChange('passcodes')}
          >
            Passcodes
          </button>
        </div>
        
        <div className="modal-content">
          {isLoading && <div className="loading-overlay">Loading...</div>}
          
          {/* Info Tab */}
          {selectedTab === 'info' && (
            <div className="info-tab">
              <div className="lock-status-panel">
                <div className={`status-indicator ${getStatusClass()}`}>
                  <div className={`status-dot ${getStatusClass()}`}></div>
                  <span>
                    {!currentLock.isConnected ? 'Disconnected' : (currentLock.isLocked ? 'Locked' : 'Unlocked')}
                  </span>
                </div>
                
                <button 
                  className={`toggle-button ${currentLock.isLocked ? 'unlock' : 'lock'}`} 
                  onClick={handleToggle}
                  disabled={!currentLock.isConnected || isLoading}
                >
                  {currentLock.isLocked ? 'Unlock' : 'Lock'}
                </button>
              </div>
              
              <div className="lock-info">
                <div className="info-item">
                  <span className="info-label">MAC Address:</span>
                  <span className="info-value">{currentLock.lockMac}</span>
                </div>
                
                <div className="info-item">
                  <span className="info-label">Battery Level:</span>
                  <span className="info-value">
                    {currentLock.batteryLevel ? `${currentLock.batteryLevel}%` : 'Unknown'}
                  </span>
                </div>
                
                <div className="info-item">
                  <span className="info-label">Connection Status:</span>
                  <span className="info-value">
                    {currentLock.isConnected ? 'Connected' : 'Disconnected'}
                  </span>
                </div>
                
                <div className="info-item">
                  <span className="info-label">Lock Status:</span>
                  <span className="info-value">
                    {currentLock.isLocked ? 'Locked' : 'Unlocked'}
                  </span>
                </div>
                
                <div className="info-item">
                  <span className="info-label">Auto Lock Time:</span>
                  <span className="info-value">
                    {currentLock.autoLockTime > 0 ? `${currentLock.autoLockTime} seconds` : 'Disabled'}
                  </span>
                </div>
              </div>
              
              <div className="lock-settings">
                <h3>Settings</h3>
                
                <form className="settings-form" onSubmit={handleUpdateAlias}>
                  <div className="form-group">
                    <label>Lock Name:</label>
                    <div className="form-group-inline">
                      <input 
                        type="text" 
                        value={lockAlias} 
                        onChange={(e) => setLockAlias(e.target.value)}
                        placeholder="Enter lock name"
                      />
                      <button 
                        type="submit" 
                        disabled={isLoading || !lockAlias.trim()}
                      >
                        Update
                      </button>
                    </div>
                  </div>
                </form>
                
                <form className="settings-form" onSubmit={handleUpdateAutoLock}>
                  <div className="form-group">
                    <label>Auto Lock Time (seconds):</label>
                    <div className="form-group-inline">
                      <input 
                        type="number" 
                        min="0" 
                        value={autoLockTime} 
                        onChange={(e) => setAutoLockTime(e.target.value)}
                        placeholder="0 = disabled"
                      />
                      <button 
                        type="submit" 
                        disabled={isLoading}
                      >
                        Update
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          )}
          
          {/* Passcodes Tab */}
          {selectedTab === 'passcodes' && (
            <div className="passcodes-tab">
              <form className="add-passcode-form" onSubmit={handleAddPasscode}>
                <h3>Add Passcode</h3>
                
                <div className="form-group">
                  <label>Passcode:</label>
                  <input 
                    type="text" 
                    value={newPasscode} 
                    onChange={(e) => setNewPasscode(e.target.value)}
                    placeholder="Enter 6-8 digit code"
                    pattern="[0-9]{6,8}"
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label>Name:</label>
                  <input 
                    type="text" 
                    value={passcodeName} 
                    onChange={(e) => setPasscodeName(e.target.value)}
                    placeholder="Enter passcode name"
                    required
                  />
                </div>
                
                <button 
                  type="submit" 
                  className="add-passcode-button"
                  disabled={isLoading || !newPasscode.trim() || !passcodeName.trim()}
                >
                  Add Passcode
                </button>
              </form>
              
              <div className="passcodes-list">
                <h3>Passcodes</h3>
                
                {passcodes.length === 0 ? (
                  <p className="no-passcodes">No passcodes added yet.</p>
                ) : (
                  <ul>
                    {passcodes.map(passcode => (
                      <li key={passcode.id} className="passcode-item">
                        <div className="passcode-info">
                          <div className="passcode-name">{passcode.name}</div>
                          <div className="passcode-code">{passcode.code}</div>
                        </div>
                        <button 
                          className="delete-passcode-button" 
                          onClick={() => handleDeletePasscode(passcode.id)}
                          disabled={isLoading}
                        >
                          Delete
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SmartLockDetails;